import React, { useContext, forwardRef } from 'react';
import { Filter as FilterIcon } from 'react-feather';
import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { shouldForwardProp } from '../../utils/emotion';

export type AnalysisFilterButtonVariant = 'analysis' | 'survey-response';

// Flag to enable white labeling for analysis variant, set to true if needed
// Added this in case Product wants to apply further white labeling in the future from what they have asked currently
const ENABLE_ANALYSIS_WHITE_LABELING = false;

type Props = {
  onClick?: () => void;
  disabled?: boolean;
  count?: number;
  variant?: AnalysisFilterButtonVariant;
  className?: string;
  'aria-label'?: string;
  type?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
};

export const AnalysisFilterButton = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    onClick,
    disabled = false,
    count = 0,
    variant = 'analysis',
    className,
    'aria-label': ariaLabel = 'Filter',
    type = 'button',
    children,
  } = props;

  const isActive = count > 0;
  const themingContext = useContext(AppThemingContext);

  const enabled = themingContext?.enabled;
  const themeColor = enabled ? themingContext.palette?.primary?.main : null;
  const hasCustomColor = !!themeColor;

  if (variant === 'survey-response') {
    // Use same styling as analysis variant to match quant button style
    const dotThemeColor = hasCustomColor ? themeColor : 'rgb(40, 170, 225)';

    return (
      <SurveyResponseButton
        ref={ref}
        onClick={disabled ? undefined : onClick}
        aria-label={ariaLabel}
        type={type}
        disabled={disabled}
        className={className}>
        <SurveyResponseIconContainer>
          <StyledFilterIcon
            height={20}
            width={20}
            $analysisVariant={true} />
          <SurveyResponseActiveCircle
            $isActive={isActive}
            $themeColor={dotThemeColor} />
        </SurveyResponseIconContainer>
        <SurveyResponseDivider />
        <SurveyResponseText>
          Filter {isActive ? `(${count})` : ''}
        </SurveyResponseText>
      </SurveyResponseButton>
    );
  }

  // Default analysis variant
  // Use theme color only if white labeling is enabled for analysis
  const analysisThemeColor = ENABLE_ANALYSIS_WHITE_LABELING && hasCustomColor ? themeColor : null;
  // For the dot, always check if white labeling is enabled (separate from the button styling)
  const dotThemeColor = hasCustomColor ? themeColor : 'rgb(40, 170, 225)';

  return (
    <AnalysisButton
      ref={ref}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      type={type}
      disabled={disabled}
      className={className}
      $themeColor={analysisThemeColor}>
      <AnalysisIconContainer>
        <StyledFilterIcon
          height={20}
          width={20}
          $analysisVariant={true}
          $themeColor={analysisThemeColor} />
        <AnalysisActiveCircle
          $isActive={isActive}
          $themeColor={dotThemeColor} />
      </AnalysisIconContainer>
      <AnalysisDivider $themeColor={analysisThemeColor} />
      <AnalysisText $themeColor={analysisThemeColor}>
        Filter {isActive ? `(${count})` : ''}
      </AnalysisText>
    </AnalysisButton>
  );
});

AnalysisFilterButton.displayName = 'AnalysisFilterButton';

// Analysis variant styles
const AnalysisButton = styled('button', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  background: 'var(--gray-xl)',
  color: $themeColor || 'var(--gray-d)',
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  border: '1px solid rgb(211, 211, 211)',
  padding: '8px 12px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    opacity: 0.8,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const AnalysisIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  marginRight: '8px',
});

const AnalysisActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$themeColor'),
})<{
  $isActive?: boolean;
  $themeColor?: string;
}>(({ $isActive, $themeColor }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: $themeColor || 'rgb(40, 170, 225)',
  right: '-2px',
  top: '-2px',
  border: '2px solid var(--gray-xl)',
  display: $isActive ? 'block' : 'none',
}));

const AnalysisDivider = styled('div', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  width: '1px',
  height: '16px',
  backgroundColor: $themeColor || 'var(--gray-d)',
  marginRight: '8px',
}));

const AnalysisText = styled('span', {
  shouldForwardProp: shouldForwardProp('$themeColor'),
})<{
  $themeColor?: string;
}>(({ $themeColor }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: $themeColor || 'var(--gray-d)',
}));

// Survey Response variant styles (matches analysis variant styling)
const SurveyResponseButton = styled.button({
  background: 'var(--gray-xl)',
  color: 'var(--gray-d)',
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  border: '1px solid rgb(211, 211, 211)',
  padding: '8px 12px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s ease',
  outline: 'none',
  '&:hover': {
    opacity: 0.9,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const SurveyResponseIconContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  height: '20px',
});

const SurveyResponseActiveCircle = styled('span', {
  shouldForwardProp: shouldForwardProp('$isActive', '$themeColor'),
})<{
  $isActive?: boolean;
  $themeColor?: string;
}>(({ $isActive, $themeColor }) => ({
  position: 'absolute',
  height: '6px',
  width: '6px',
  borderRadius: '50%',
  backgroundColor: $themeColor || 'rgb(40, 170, 225)',
  right: '-2px',
  top: '-2px',
  border: '2px solid var(--gray-xl)',
  display: $isActive ? 'block' : 'none',
}));

const SurveyResponseDivider = styled.div({
  width: '1px',
  height: '16px',
  backgroundColor: 'var(--gray-d)',
});

const SurveyResponseText = styled.span({
  fontSize: '1rem',
  fontWeight: 500,
  color: 'var(--gray-d)',
});

// Shared icon styles
const StyledFilterIcon = styled(FilterIcon, {
  shouldForwardProp: shouldForwardProp('$themeColor', '$analysisVariant'),
})<{
  $themeColor?: string;
  $analysisVariant?: boolean;
}>(({ $themeColor, $analysisVariant }) => {
  // For analysis variant, use gray-d unless theme color is provided
  if ($analysisVariant) {
    const iconColor = $themeColor || 'var(--gray-d)';
    return {
      color: iconColor,
      stroke: iconColor,
      fill: 'none',
    };
  }

  // For other variants, use existing logic
  const iconColor = 'var(--gray-d)';
  return {
    color: $themeColor || iconColor,
    stroke: $themeColor || iconColor,
    fill: 'none',
  };
});
